import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import {
	useLazyDownloadBulkPurchaseTemplateQuery,
	useValidateXlsxBulkPurchaseMutation,
} from "../../../../../service/bulk-purchase-service";
import { BulkLineItem, rowToLineItem } from "../../../../../model/bulk-purchase/bulk-purchase-customer";
import { getErrorMessage } from "../../../../../utils/error-handler";
import styles from "./upload-tab.module.scss";

interface UploadTabProps {
	/** Called with the parsed + validated rows returned by the backend. */
	onValidated: (items: BulkLineItem[], errors: string[]) => void;
}

const MAX_SIZE_MB = 10;

const UploadCloudIcon: React.FC = () => (
	<svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
		<path d="M12 15V4m0 0 4 4m-4-4L8 8" stroke="var(--app-secondary)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M4 15v2a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-2" stroke="var(--app-secondary)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const DownloadIcon: React.FC = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
		<path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const UploadTab: React.FC<UploadTabProps> = ({ onValidated }) => {
	const { t } = useTranslation();
	const inputRef = useRef<HTMLInputElement>(null);
	const [fileName, setFileName] = useState<string | null>(null);
	const [downloadTemplate, { isFetching: isDownloading }] = useLazyDownloadBulkPurchaseTemplateQuery();
	const [validateXlsx, { isLoading: isValidating }] = useValidateXlsxBulkPurchaseMutation();

	const handleDownload = async () => {
		try {
			await downloadTemplate().unwrap();
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? t("buyElectricity.bulk.failedToDownloadTemplate"));
		}
	};

	const handleFile = async (file: File) => {
		if (file.size > MAX_SIZE_MB * 1024 * 1024) {
			toast.warn(t("buyElectricity.bulk.fileTooLarge", { size: MAX_SIZE_MB }));
			return;
		}
		setFileName(file.name);
		try {
			const response = await validateXlsx(file).unwrap();
			const items = (response.rows ?? []).map(row => rowToLineItem(row, uuidv4()));
			onValidated(items, response.errors ?? []);
			if (!items.length) {
				toast.error(t("buyElectricity.bulk.noRowsFound"));
			}
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? t("buyElectricity.bulk.bulkItemsValidationFailed"));
		}
	};

	const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) handleFile(file);
		e.target.value = "";
	};

	return (
		<div className={styles.container}>
			<button type="button" className={styles.templateBtn} onClick={handleDownload} disabled={isDownloading}>
				<DownloadIcon />
				<span>{isDownloading ? t("buyElectricity.bulk.downloading") : t("buyElectricity.bulk.downloadTemplateShort")}</span>
			</button>
			<p className={styles.hint}>{t("buyElectricity.bulk.downloadTemplate")}</p>

			<button
				type="button"
				className={styles.dropzone}
				onClick={() => inputRef.current?.click()}
				disabled={isValidating}
			>
				<UploadCloudIcon />
				<span className={styles.dropTitle}>
					{isValidating
						? t("buyElectricity.bulk.validatingUploadedFileFormat")
						: fileName ?? t("buyElectricity.bulk.uploadXlsx")}
				</span>
				<span className={styles.dropSub}>{t("buyElectricity.bulk.xlsxOnlyMax", { size: MAX_SIZE_MB })}</span>
			</button>

			<input
				ref={inputRef}
				type="file"
				accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
				hidden
				onChange={onInputChange}
			/>
		</div>
	);
};

export default UploadTab;
